import { useEffect, useState } from "react";
import { CalendarDays, MapPin, Car, Plus, Loader2, Check, X, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { priceFor, sizeLabel } from "@/lib/pricing";
import { statusClass } from "@/pages/AdminDashboard";
import AddToCalendar from "@/components/AddToCalendar";
import { Switch } from "@/components/ui/switch";

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function PortalBookingCard({ booking: b, addons, onChanged }) {
  const [mode, setMode] = useState("");
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState(todayPlus(2));
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [picked, setPicked] = useState([]);

  useEffect(() => {
    if (mode !== "reschedule") return;
    api.get("/availability", { params: { date } }).then((r) => setSlots(r.data.slots || []));
  }, [mode, date]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      setMode("");
      setPicked([]);
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const available = addons.filter((a) => !(b.addons || []).some((x) => x.id === a.id));
  const pickedTotal = available.filter((a) => picked.includes(a.id)).reduce((s, a) => s + priceFor(a, b.vehicle.size), 0);
  const past = b.hours_until < 0 || ["completed", "cancelled", "declined"].includes(b.status);

  return (
    <div className="cj-card p-5 sm:p-6" data-testid={`portal-booking-${b.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-display uppercase font-extrabold text-2xl text-slate-100">{b.service_name}</div>
          <div className="font-mono text-xs text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {b.scheduled_date} · {b.scheduled_time}</span>
            <span className="inline-flex items-center gap-1"><Car className="h-3 w-3" /> {b.vehicle.year} {b.vehicle.make} {b.vehicle.model} · {sizeLabel(b.vehicle.size)}</span>
            <span className="inline-flex items-center gap-1 truncate max-w-xs"><MapPin className="h-3 w-3" /> {b.customer.address}</span>
          </div>
        </div>
        <span className={`cj-pill border ${statusClass(b.status)}`} data-testid={`portal-status-${b.id}`}>{b.status.replace("_", " ")}</span>
      </div>

      {b.quote_status === "sent" && (
        <div className="mt-4 p-4 rounded-xl border border-cyan-500/30 bg-cyan-500/5" data-testid={`portal-quote-card-${b.id}`}>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300">Quote from CJ</div>
          <div className="flex items-end justify-between gap-4 mt-1 flex-wrap">
            <div>
              <div className="font-display font-extrabold text-3xl text-slate-100">${b.quoted_price?.toFixed(2)}</div>
              {b.quote_note && <div className="text-sm text-slate-300 mt-1">{b.quote_note}</div>}
            </div>
            <div className="flex gap-2">
              <button disabled={busy} onClick={() => act(() => api.post(`/portal/bookings/${b.id}/quote`, { action: "accept" }), "Quote accepted — see you soon!")} data-testid={`portal-quote-accept-${b.id}`} className="cj-btn-primary px-4 py-2 rounded-full text-sm inline-flex items-center gap-1"><Check className="h-4 w-4" /> Accept</button>
              <button disabled={busy} onClick={() => act(() => api.post(`/portal/bookings/${b.id}/quote`, { action: "decline" }), "Quote declined — CJ has been notified")} data-testid={`portal-quote-decline-${b.id}`} className="px-4 py-2 rounded-full text-sm text-red-300 border border-red-500/30 hover:bg-red-500/10 inline-flex items-center gap-1"><X className="h-4 w-4" /> Decline</button>
            </div>
          </div>
        </div>
      )}
      {b.quote_status === "accepted" && <div className="mt-3 text-xs text-emerald-300 font-mono" data-testid={`portal-quote-accepted-${b.id}`}>✓ Quote accepted · ${b.quoted_price?.toFixed(2)}</div>}

      <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
        <div className="rounded-lg bg-[#0f1522] border border-white/5 p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Extras</div>
          <div className="text-slate-200 mt-1">{b.addons?.length ? b.addons.map((a) => a.name).join(", ") : "None"}</div>
        </div>
        <div className="rounded-lg bg-[#0f1522] border border-white/5 p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Deposit</div>
          <div className="text-slate-200 mt-1">${b.deposit_amount} · {b.payment_status}</div>
        </div>
        <div className="rounded-lg bg-[#ff6b00]/10 border border-[#ff6b00]/20 p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Balance on the day</div>
          <div className="font-display font-extrabold text-xl text-[#ff6b00] mt-0.5" data-testid={`portal-balance-${b.id}`}>${b.final_balance.toFixed(2)}</div>
        </div>
      </div>

      {b.payment_status !== "paid" && ["pending", "awaiting_deposit"].includes(b.status) && (
        <a
          href={`/pay/${b.id}`}
          data-testid={`portal-pay-deposit-${b.id}`}
          className="mt-4 cj-btn-primary w-full px-5 py-3 rounded-full text-sm inline-flex items-center justify-center gap-2 font-bold"
        >
          Pay ${b.deposit_amount} deposit to lock your slot
        </a>
      )}

      {b.status === "completed" && !b.balance_paid && b.final_balance > 0 && (
        <a
          href={`/pay-balance/${b.id}`}
          data-testid={`portal-pay-balance-${b.id}`}
          className="mt-4 cj-btn-primary w-full px-5 py-3 rounded-full text-sm inline-flex items-center justify-center gap-2 font-bold"
        >
          Pay outstanding balance (${b.final_balance.toFixed(2)})
        </a>
      )}
      {b.status === "completed" && b.balance_paid && (
        <div className="mt-4 text-sm text-emerald-300 font-mono inline-flex items-center gap-2" data-testid={`portal-balance-paid-${b.id}`}>
          ✓ Balance paid in full — thank you!
        </div>
      )}

      {!past && (
        <div className="mt-4">
          {b.editable ? (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setMode(mode === "reschedule" ? "" : "reschedule")} data-testid={`portal-reschedule-button-${b.id}`} className="cj-btn-ghost px-4 py-2 rounded-full text-xs inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Reschedule</button>
              {available.length > 0 && <button onClick={() => setMode(mode === "extras" ? "" : "extras")} data-testid={`portal-add-extras-button-${b.id}`} className="cj-btn-ghost px-4 py-2 rounded-full text-xs inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add extras</button>}
            </div>
          ) : (
            <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 text-xs text-amber-200 inline-flex items-center gap-2" data-testid={`portal-locked-${b.id}`}>
              <Lock className="h-3.5 w-3.5" /> {b.status === "waitlisted"
                ? "Waiting on CJ to confirm this request."
                : (b.payment_status !== "paid" && ["pending", "awaiting_deposit"].includes(b.status)
                    ? "Pay your deposit above to lock this slot and unlock changes."
                    : "Within 24 h of your detail — changes are locked. Call or email CJ for help.")}
            </div>
          )}

          {mode === "reschedule" && (
            <div className="mt-4 p-4 rounded-xl border border-white/10 bg-[#0f1522]" data-testid={`portal-reschedule-panel-${b.id}`}>
              <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                {Array.from({ length: 14 }).map((_, i) => {
                  const d = todayPlus(i + 2);
                  return (
                    <button key={d} onClick={() => { setDate(d); setTime(""); }} data-testid={`portal-date-${d}`} className={`px-2 py-2 rounded-lg border text-xs font-mono ${d === date ? "bg-[#ff6b00] text-[#0b0f17] border-[#ff6b00]" : "border-white/10 text-slate-300"}`}>{d.slice(5)}</button>
                  );
                })}
              </div>
              <div className="mt-3 grid grid-cols-4 gap-2">
                {slots.map((s) => (
                  <button key={s.time} disabled={!s.available} onClick={() => setTime(s.time)} data-testid={`portal-slot-${s.time.replace(":", "")}`} className={`px-2 py-2 rounded-lg border text-xs font-mono ${time === s.time ? "bg-[#ff6b00] text-[#0b0f17] border-[#ff6b00]" : s.available ? "border-white/10 text-slate-200" : "border-white/5 text-slate-600 line-through"}`}>{s.time}</button>
                ))}
              </div>
              <button disabled={!time || busy} onClick={() => act(() => api.patch(`/portal/bookings/${b.id}/reschedule`, { scheduled_date: date, scheduled_time: time }), "Rescheduled!")} data-testid={`portal-reschedule-confirm-${b.id}`} className="cj-btn-primary mt-4 px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Confirm {date} {time}
              </button>
            </div>
          )}

          {mode === "extras" && (
            <div className="mt-4 rounded-xl border border-white/10 overflow-hidden" data-testid={`portal-extras-panel-${b.id}`}>
              {available.map((a) => {
                const on = picked.includes(a.id);
                return (
                  <label key={a.id} className={`flex items-center justify-between gap-4 px-4 py-3 border-b border-white/5 last:border-b-0 cursor-pointer ${on ? "bg-[#00f0ff]/5" : ""}`}>
                    <div>
                      <div className="font-display uppercase font-bold text-sm text-slate-100">{a.name}</div>
                      <div className="text-xs text-slate-400">{a.description}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`font-display font-extrabold ${on ? "text-[#00f0ff]" : "text-slate-400"}`}>+${priceFor(a, b.vehicle.size).toFixed(0)}</span>
                      <Switch checked={on} onCheckedChange={() => setPicked((p) => (p.includes(a.id) ? p.filter((x) => x !== a.id) : [...p, a.id]))} data-testid={`portal-extra-switch-${a.slug}`} className="data-[state=checked]:bg-[#00f0ff]" />
                    </div>
                  </label>
                );
              })}
              <div className="px-4 py-3 bg-[#0f1522] flex items-center justify-between">
                <span className="text-sm text-slate-300">Adds <span className="font-mono text-[#00f0ff]">+${pickedTotal.toFixed(2)}</span> to your balance on the day</span>
                <button disabled={!picked.length || busy} onClick={() => act(() => api.post(`/portal/bookings/${b.id}/addons`, { addon_ids: picked }), "Extras added!")} data-testid={`portal-extras-confirm-${b.id}`} className="cj-btn-primary px-4 py-2 rounded-full text-sm disabled:opacity-50">Add to booking</button>
              </div>
            </div>
          )}

          {b.cancellable && (
            <button onClick={() => setMode(mode === "cancel" ? "" : "cancel")} data-testid={`portal-cancel-button-${b.id}`} className="mt-2 px-4 py-2 rounded-full text-xs text-red-300 border border-red-500/30 hover:bg-red-500/10 inline-flex items-center gap-1"><X className="h-3.5 w-3.5" /> Cancel booking</button>
          )}
          {mode === "cancel" && (
            <div className="mt-3 p-4 rounded-xl border border-red-500/30 bg-red-500/5" data-testid={`portal-cancel-panel-${b.id}`}>
              <div className="text-sm text-slate-200 font-display uppercase font-bold">Cancel this booking?</div>
              <div className="text-xs text-slate-300 mt-1">
                {b.payment_status === "paid"
                  ? (b.hours_until > 24
                      ? `You're more than 24 h out — your $${b.deposit_amount} deposit will be refunded in full.`
                      : `You're within 24 h of your appointment — your $${b.deposit_amount} deposit is non-refundable.`)
                  : "No deposit has been taken, so there's nothing to refund."}
              </div>
              <div className="mt-3 flex gap-2">
                <button disabled={busy} onClick={() => act(() => api.post(`/portal/bookings/${b.id}/cancel`), "Booking cancelled")} data-testid={`portal-cancel-confirm-${b.id}`} className="px-4 py-2 rounded-full text-sm text-red-200 border border-red-500/40 hover:bg-red-500/10 inline-flex items-center gap-1">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />} Yes, cancel</button>
                <button onClick={() => setMode("")} className="px-4 py-2 rounded-full text-sm cj-btn-ghost">Keep it</button>
              </div>
            </div>
          )}
        </div>
      )}

      {!past && ["deposit_paid", "confirmed"].includes(b.status) && (
        <div className="mt-4"><AddToCalendar booking={b} /></div>
      )}
    </div>
  );
}
